/**
 * FreeShippingBadge, tiny, reusable UI component that surfaces the site-wide
 * promise "Free shipping on orders over €100 (or equivalent)" in whatever
 * currency the user has selected. When a `subtotalEur` is passed in, it also
 * shows how much more the user needs to spend ("Add €12 more for free
 * shipping") and flips to a green "You've got free shipping!" state when the
 * threshold is reached.
 */
import React from 'react';
import { Truck, CheckCircle2 } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { useLanguage } from '../contexts/LanguageContext';

export const FREE_SHIPPING_THRESHOLD_EUR = 100;
export const FLAT_SHIPPING_RATE_EUR = 25;

const COPY = {
  en: {
    over: (p) => `Free shipping on orders over ${p}`,
    flat: (rate, threshold) => `Shipping: ${rate} under ${threshold} · Free above`,
    unlocked: 'You\'ve unlocked free shipping!',
    addMore: (amt) => `Add ${amt} more for free shipping`,
  },
  es: {
    over: (p) => `Envío gratis en pedidos superiores a ${p}`,
    flat: (rate, threshold) => `Envío: ${rate} hasta ${threshold} · Gratis por encima`,
    unlocked: '¡Has obtenido envío gratis!',
    addMore: (amt) => `Añade ${amt} más para envío gratis`,
  },
  ru: {
    over: (p) => `Бесплатная доставка от ${p}`,
    flat: (rate, threshold) => `Доставка: ${rate} до ${threshold} · Бесплатно свыше`,
    unlocked: 'Вы получили бесплатную доставку!',
    addMore: (amt) => `Добавьте ещё ${amt} для бесплатной доставки`,
  },
  de: {
    over: (p) => `Kostenloser Versand ab ${p}`,
    flat: (rate, threshold) => `Versand: ${rate} unter ${threshold} · Gratis darüber`,
    unlocked: 'Sie haben kostenlosen Versand freigeschaltet!',
    addMore: (amt) => `Noch ${amt} bis zum kostenlosen Versand`,
  },
  fr: {
    over: (p) => `Livraison gratuite pour les commandes de plus de ${p}`,
    flat: (rate, threshold) => `Livraison : ${rate} en dessous de ${threshold} · Gratuite au-dessus`,
    unlocked: 'Vous avez débloqué la livraison gratuite !',
    addMore: (amt) => `Ajoutez ${amt} de plus pour la livraison gratuite`,
  },
  it: {
    over: (p) => `Spedizione gratuita per ordini superiori a ${p}`,
    flat: (rate, threshold) => `Spedizione: ${rate} sotto ${threshold} · Gratis sopra`,
    unlocked: 'Hai sbloccato la spedizione gratuita!',
    addMore: (amt) => `Aggiungi ${amt} per la spedizione gratuita`,
  },
  pl: {
    over: (p) => `Darmowa wysyłka dla zamówień powyżej ${p}`,
    flat: (rate, threshold) => `Wysyłka: ${rate} poniżej ${threshold} · Gratis powyżej`,
    unlocked: 'Odblokowałeś darmową wysyłkę!',
    addMore: (amt) => `Dodaj jeszcze ${amt}, aby uzyskać darmową wysyłkę`,
  },
  nl: {
    over: (p) => `Gratis verzending bij bestellingen boven ${p}`,
    flat: (rate, threshold) => `Verzending: ${rate} onder ${threshold} · Gratis boven`,
    unlocked: 'Je hebt gratis verzending ontgrendeld!',
    addMore: (amt) => `Voeg ${amt} toe voor gratis verzending`,
  },
  pt: {
    over: (p) => `Frete grátis em pedidos acima de ${p}`,
    flat: (rate, threshold) => `Envio: ${rate} abaixo de ${threshold} · Grátis acima`,
    unlocked: 'Você desbloqueou o frete grátis!',
    addMore: (amt) => `Adicione ${amt} para frete grátis`,
  },
  au: {
    over: (p) => `Free shipping on orders over ${p}`,
    flat: (rate, threshold) => `Shipping: ${rate} under ${threshold} · Free above`,
    unlocked: 'You\'ve unlocked free shipping!',
    addMore: (amt) => `Add ${amt} more for free shipping`,
  },
};

export default function FreeShippingBadge({
  subtotalEur = null,
  variant = 'inline', // 'inline' | 'pill' | 'banner'
  testId = 'free-shipping-badge',
}) {
  const { formatPrice } = useCurrency();
  const { currentLanguage } = useLanguage();
  const t = COPY[currentLanguage] || COPY.en;

  const threshold = FREE_SHIPPING_THRESHOLD_EUR;
  const flatRate = FLAT_SHIPPING_RATE_EUR;
  const thresholdStr = formatPrice(threshold);
  const flatRateStr = formatPrice(flatRate);

  const reached = subtotalEur != null && Number(subtotalEur) >= threshold;
  const remaining =
    subtotalEur != null && !reached
      ? Math.max(0, threshold - Number(subtotalEur))
      : null;

  if (variant === 'pill') {
    return (
      <span
        data-testid={testId}
        className={`inline-flex items-center gap-1 text-[11px] md:text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
          reached
            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
            : 'bg-capsule-teal/10 text-capsule-teal border border-capsule-teal/30'
        }`}
      >
        {reached
          ? <><CheckCircle2 className="w-3 h-3" /> {t.unlocked}</>
          : <><Truck className="w-3 h-3" /> {t.flat(flatRateStr, thresholdStr)}</>}
      </span>
    );
  }

  if (variant === 'banner') {
    return (
      <div
        data-testid={testId}
        className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${
          reached
            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
            : 'bg-capsule-teal/5 text-capsule-teal border border-capsule-teal/20'
        }`}
      >
        {reached ? <CheckCircle2 className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
        <span>
          {reached
            ? t.unlocked
            : remaining != null
            ? t.addMore(formatPrice(remaining))
            : t.flat(flatRateStr, thresholdStr)}
        </span>
      </div>
    );
  }

  // inline
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 text-xs md:text-sm font-medium ${
        reached ? 'text-emerald-600' : 'text-capsule-teal'
      }`}
    >
      {reached ? <CheckCircle2 className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
      {reached
        ? t.unlocked
        : remaining != null
        ? t.addMore(formatPrice(remaining))
        : t.flat(flatRateStr, thresholdStr)}
    </span>
  );
}
